<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref, toRaw } from "vue";
import * as Sentry from "@sentry/vue";

import { useToast } from "vue-toastification";
import { useRoute } from "vue-router";

import { deleteSpeckleMaterial, removeMaterial, UpdateMaterialPropertiesRequest } from "@/api/services/Mapping";
import { MaterialProperties } from "@/types/Views/MappingWizard/MaterialProperties";

import { useProject } from "@/stores/project/project.store";
import { useModalStore } from "@/stores/modals/modals.store";
import { newMappingStore } from "@/stores/Views/MappingWizard/NewMappingStore";

import { Loader2 } from "@lucide/vue";
import Tabs from "@/components/Reusables/Tabs/Tabs.vue";
import EpdUsedBox from "./EpdUsedBox.vue";
import MaterialKpiStrip from "./MaterialKpiStrip.vue";
import { getDeclaredEpdSource } from "./declaredEpdSource";
import { resolveDeclaredUnitSave } from "./declaredUnitSave";
import { resolveIdentifierRename } from "./identifierRename";
import { resolveSavedMaterial } from "./savedMaterialResult";
import UnitTable from "@/components/Views/MaterialMapping/UnitTable/UnitTable.vue";
import EmissionsPanel from "./EmissionsPanel.vue";
import MaterialBasicsSection from "../MaterialEditModal/MaterialBasicsSection.vue";
import MaterialStageSection from "../MaterialEditModal/MaterialStageSection.vue";
import OriginTag from "@/components/Views/MaterialMapping/OriginTag.vue";
import LibraryDetailItem from "@/views/Library/LibraryDetailItem.vue";
import LifetimeTable from "@/components/Views/MaterialMapping/LifetimeTable/LifetimeTable.vue";

import { useDashboard } from "@/stores/dashboard/dashboard.store";
import {
  findProjectMaterialRow,
  sumMaterialTotal,
  useHotspotBreakdown,
} from "@/features/Dashboard/composables/useHotspotBreakdown";
import { ALL_STAGES_SUMMARY } from "@/features/Dashboard/stageFilterSummary";
import { useEnum } from "@/stores/project/project.enum";
import { Country_Enum, useLocalization } from "@/providers/Localization/index.ts";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";

const route = useRoute();

const dashboardStore = useDashboard();
const { refreshDashboard } = dashboardStore;
const { dashboard } = storeToRefs(dashboardStore);
const { getProjectMaterialRows, scopeToSelectedStages, stageScopeSummary } = useHotspotBreakdown();

const projectStore = useProject();
const { fetchProjectMaterials } = projectStore;
const { autoMappingPreviewLoaded, isGroupingByType, selectedMaterial, project } = storeToRefs(projectStore);
const localization = useLocalization();
const { country } = storeToRefs(localization);

const modals = useModalStore();

const isMissingUnit = ref<boolean>(false);

const useMappingStore = newMappingStore();

const props = defineProps<{
  material: any;
  materialId: string;
  /** The inventory group this material was opened from, already named the way its row names itself. */
  parentName?: string | null;
  /** What that group is — "Material" or "Type". Falls back to the current grouping when unset. */
  parentLabel?: string | null;
}>();

const cloneMaterial = ref(JSON.parse(JSON.stringify(toRaw(props.material))));
const originalIdentifier: string = String(props.material?.identifier ?? "");

const enumStore = useEnum();
const { getMassUnit } = enumStore;

const getMassUnitItem = computed(() => getMassUnit(cloneMaterial.value.mapping.declaredUnit.massUnit));

// Must read through the same getProjectMaterialRows + findProjectMaterialRow + sumMaterialTotal +
// scopeToSelectedStages pipeline as the Emissions tab's Project series, or the header and tab disagree.
const materialTotalGwp = computed<number | null>(() => {
  const row = findProjectMaterialRow(getProjectMaterialRows(), props.materialId, props.material.identifier);
  return row ? sumMaterialTotal(scopeToSelectedStages(row.rawStages)) : null;
});

const stageScopeNote = computed<string | null>(() =>
  stageScopeSummary.value === ALL_STAGES_SUMMARY ? null : `Scoped to the Stages filter (${stageScopeSummary.value}).`
);

const enableUnitIds = ref<number[]>([]);

onBeforeMount(() => {
  if (!cloneMaterial.value.mapping) return;

  const units = cloneMaterial.value.totalDeclaredUnits ?? [];

  const libraryDeclaredUnit = cloneMaterial.value.mapping.declaredUnit?.declaredUnit;

  const existingUnit = units.find((unit: any) => unit.declaredUnit === libraryDeclaredUnit);

  if (existingUnit) {
    enableUnitIds.value.push(existingUnit.declaredUnit);
  }

  const existingMassUnit = units.find((unit: any) => unit.declaredUnit === getMassUnitItem.value?.declaredUnit);

  if (existingMassUnit) {
    enableUnitIds.value.push(existingMassUnit.declaredUnit);
  }
});

const lifeTimeErrors = ref<Array<string>>([]);
const lifeTimeTableIsHidden = ref<boolean>(false);
const alternateBimTypeCode = ref<string>("");

const isSaving = ref<boolean>(false);
const isBusy = ref<boolean>(false);
const isDelete = ref<boolean>(false);

// Active tab. Emissions is only meaningful once a mapping exists.
const activeTab = ref<string>("overview");

const tabs = computed(() => [
  { key: "overview", label: "Overview" },
  // Labelled "Quantity", not "Units", to match the column inside it. The key stays `units` so the
  // slot name and any stored tab state keep resolving.
  { key: "units", label: "Quantity" },
  { key: "emissions", label: "Emissions", disabled: !cloneMaterial.value.mapping },
]);

const materialMappingIsType = computed(() => {
  return selectedMaterial.value === "type";
});

// This view's kind and its parent group's kind are always opposites, so both come off one flag.
// The caller states the parent's kind when it knows it; the store answers when it doesn't.
const parentKindLabel = computed(() => props.parentLabel || (materialMappingIsType.value ? "Type" : "Material"));

// Sits outside `Tabs` so it stays on screen on the Quantity and Emissions tabs too. Empty for any
// caller that has no parent to name, which renders nothing.
const parentBreadcrumb = computed(() => (props.parentName ? `${parentKindLabel.value} · ${props.parentName}` : ""));

const disableBtn = computed(() => {
  return isBusy.value || isDelete.value || isSaving.value;
});

// Stays fully readable in both read-only cases (previewing a frozen revision; Viewer role) but nothing
// may be committed. `useProjectEditReadOnly` folds both into one flag, worded correctly for each.
const { isReadOnly, readOnlyTooltip, blockedByPreview } = useProjectEditReadOnly("top");

/** Drives the editable fields: a view that is allowed to be saved, by role and by revision. */
const canEdit = computed(() => !isReadOnly.value);

// New-design token buttons (match the mapping surface / EpdUsedBox).
const btnBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
const btnPrimary = "bg-app-blue-600 dark:bg-app-blue-fill text-white hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover";
const btnNeutral = "border border-slate-200 bg-slate-0 text-type-default hover:bg-slate-100";
const btnDestructive = "bg-app-red-600 dark:bg-app-red-fill text-white hover:bg-app-red-700 dark:hover:bg-app-red-fill-hover";

// The Classification field reads the configured parameter before `BIMTypeCode`, so the pick goes there too.
// `CCSClassification` is left alone: it travels in the save payload.
const handleSetNewBimCode = (newBimCode: string) => {
  cloneMaterial.value.additionalParameters.BIMTypeCode = newBimCode;
  const speckleParameter = project.value?.speckleConfiguration?.parameters?.classificationCode;
  if (speckleParameter) cloneMaterial.value.additionalParameters[speckleParameter] = newBimCode;
};

const onCCSClassification = (classification: any) => {
  const { CCSClassification, classificationObjectClass } = classification;

  const speckleParameter = project.value!.speckleConfiguration.parameters.classificationCode;
  Object.assign(cloneMaterial.value.additionalParameters, {
    BIMTypeCode: CCSClassification,
    classificationObjectClass,
    [speckleParameter]: CCSClassification,
  });
};

// Countries that have their own built-in lifetime table (rendered inline as
// `LifetimeTable`) rather than the buildingPartServiceLifetime-based picker.
const COUNTRIES_WITHOUT_LIFETIME_PICKER = [Country_Enum.DENMARK];

const toggleLifetimeTableIsHidden = async () => {
  // Picking a lifetime writes it onto the material, so the table is a write path like the rest.
  if (blockedByPreview()) return;

  // For lifetime selector based on buildingPartServiceLifetime
  if (country.value && !COUNTRIES_WITHOUT_LIFETIME_PICKER.includes(country.value)) {
    const speckleParameter = "BIMTypeCode";
    const classificationCode = speckleParameter ? cloneMaterial.value?.additionalParameters[speckleParameter] : null;
    const materialCategoryId = cloneMaterial.value?.mapping?.category;

    const result = await modals.open(
      "MaterialServiceLifetimeSelectorModal",
      { country: country.value, classificationCode, materialCategoryId },
      { stackable: true, wrapperClass: "max-w-2xl  h-full" }
    );

    if (result && result.lifetime) {
      handleLifeTimeSelected(result.lifetime);
    }
  } else {
    // Lifetime selector for Denmark (inline LifetimeTable)
    lifeTimeTableIsHidden.value = !lifeTimeTableIsHidden.value;
  }
};

function updateIsRecycledValue(newIsRecycled: boolean) {
  cloneMaterial.value.isRecycled = newIsRecycled;
}

function updateStageValue(newStage: number) {
  cloneMaterial.value.stage = newStage;
}

const toggleDeleteModal = async () => {
  if (blockedByPreview()) return;

  const modal = await modals.open(
    "ModalConfirm",
    {
      heading: "Are you sure?",
      message: "By clicking delete the entity will be deleted",
    },
    { stackable: true, wrapperClass: "w-fit! h-fit! min-h-[180px]!" }
  );

  if (modal && modal.action === "confirm") {
    isDelete.value = true;
    await deleteSpeckleMaterial(String(route.params.id), cloneMaterial.value.id);
    refreshDashboard();
    await fetchProjectMaterials(String(route.params.id), materialMappingIsType.value);
    useToast().success("Your entity has been deleted");
    modals.close(cloneMaterial.value);
  }
};

const submitSave = async () => {
  if (blockedByPreview()) return;

  // A mapped material cannot save without a preferred unit (the EPD scales by it); an unmapped one has
  // no EPD to scale, so the requirement doesn't hold its other fields hostage.
  const declaredUnits = resolveDeclaredUnitSave({
    units: cloneMaterial.value.totalDeclaredUnits,
    isMapped: cloneMaterial.value.mapping != null,
  });

  isMissingUnit.value = declaredUnits.blocked;

  if (declaredUnits.blocked) {
    // The error renders inside UnitTable, so open the tab that holds it — a toast on its own left
    // the Overview tab looking like the Save had simply done nothing.
    activeTab.value = "units";
    useToast().error("Select a preferred unit before saving this mapping.");
    return;
  }

  const editedIdentifier = String(cloneMaterial.value.identifier ?? "").trim();
  if (!editedIdentifier) {
    activeTab.value = "overview";
    useToast().error(`Enter a ${materialMappingIsType.value ? "material" : "type"} name before saving.`);
    return;
  }
  cloneMaterial.value.identifier = editedIdentifier;

  isSaving.value = true;

  try {
    const [firstUnit, ...extraUnits] = declaredUnits.requests;
    const rename = resolveIdentifierRename(originalIdentifier, editedIdentifier, isGroupingByType.value);

    let payload: MaterialProperties[] = [
      {
        materialId: props.materialId,
        lifeTime: cloneMaterial.value.lifeTime,
        lifetimeOffset: cloneMaterial.value.lifeTimeOffset,
        ...(firstUnit && { declaredUnitRequest: firstUnit }),
        description: cloneMaterial.value.description,
        isReused: cloneMaterial.value.isRecycled,
        stage: cloneMaterial.value.stage,
        bimTypeCode: cloneMaterial.value.additionalParameters?.BIMTypeCode,
        CCIClassification: cloneMaterial.value.additionalParameters?.CCIClassification,
        CCSClassification: cloneMaterial.value.additionalParameters?.CCSClassification,
        classificationObjectClass: cloneMaterial.value.additionalParameters?.classificationObjectClass,
        ...rename,
      },
    ];

    // The name travels only when the user changed it (resolveIdentifierRename); an unchanged round-trip was a rename risk.
    // One entry carries one declared unit; extra rows hold only that unit, everything else stays on the first entry.
    payload.push(...extraUnits.map((declaredUnitRequest) => ({ materialId: props.materialId, declaredUnitRequest })));

    const response = await UpdateMaterialPropertiesRequest(String(route.params.id), isGroupingByType.value, payload);
    refreshDashboard();

    const savedRow =
      response && typeof response === "object" && "materials" in response
        ? response.materials.find((row) => row.id === props.materialId)
        : undefined;
    const renameLost = Object.keys(rename).length > 0 && savedRow !== undefined && savedRow.identifier !== editedIdentifier;
    if (renameLost) {
      Sentry.captureException(
        new Error(`Material ${props.materialId} rename to "${editedIdentifier}" came back as "${savedRow?.identifier}"`)
      );
      useToast().error("The name was not saved. Reload the page and try again.");
    } else {
      useToast().success("Material saved successfully");
    }
    modals.close(resolveSavedMaterial(cloneMaterial.value, savedRow));
  } catch (error) {
    console.error("Error saving material:", error);
    useToast().error("Failed to save material. Please try again.");
  } finally {
    isSaving.value = false;
  }
};

const handleLifeTimeSelected = (value: string) => {
  cloneMaterial.value.lifeTime = value;
  lifeTimeTableIsHidden.value = false;
};

const removeMapping = async () => {
  if (blockedByPreview()) return;

  isBusy.value = true;
  await removeMaterial(String(route.params.id), cloneMaterial.value.id);
  refreshDashboard();
  cloneMaterial.value.mapping = null;
  modals.close(cloneMaterial.value);
};

// Inspecting an EPD swaps this modal's content for the EPD view rather than opening another modal. A
// stack, not a single ref, so drilling EPD -> EPD walks back one step at a time.
const inspectStack = ref<any[]>([]);
const inspectedEpd = computed(() => inspectStack.value.at(-1) ?? null);

const isInspecting = ref<boolean>(false);

// Reads the EPD service record, NOT `cloneMaterial.mapping` (whose lciaResults are project-scaled, not
// declared). Fetch-then-push: LCIAChart/StagesTableModal read their data on mount, so it must exist first.
const handleMappingNameClicked = async () => {
  const mapping = cloneMaterial.value.mapping;
  if (!mapping || isInspecting.value) return;

  isInspecting.value = true;
  try {
    const declaredSource = await getDeclaredEpdSource(mapping);

    // Never fall back to the mapping payload — presenting those numbers as declared values is the
    // defect. Leave the form on screen and say so instead.
    if (!declaredSource) {
      Sentry.captureException(new Error(`Could not load declared EPD data for mapping ${mapping.id ?? "(no id)"}`));
      useToast().error("Couldn't load this EPD's declared data. Try again in a moment.");
      return;
    }

    inspectStack.value.push(declaredSource);
  } finally {
    isInspecting.value = false;
  }
};

const autoMappingLifeTime = computed(() => {
  if (autoMappingPreviewLoaded.value) {
    return cloneMaterial.value.lifeTime;
  }

  return undefined;
});
</script>

<template>
  <div class="flex h-full flex-col">
    <div
      v-if="!lifeTimeTableIsHidden && !inspectedEpd"
      class="flex min-h-0 flex-1 flex-col">
      <div class="shrink-0 pb-4">
        <p
          v-if="parentBreadcrumb"
          v-tooltip.truncated="parentBreadcrumb"
          class="t-caption text-type-muted truncate whitespace-nowrap">
          {{ parentBreadcrumb }}
        </p>
        <h4 class="t-sh4 text-type-default">{{ material.identifier }}</h4>
        <MaterialKpiStrip
          class="mt-2"
          :material="cloneMaterial"
          :total-gwp="materialTotalGwp"
          :stage-scope-note="stageScopeNote"
          :gwp-unit="dashboard?.unit?.label ?? ''" />
      </div>

      <Tabs
        v-model="activeTab"
        :tabs="tabs"
        variant="underline"
        class="flex min-h-0 flex-1 flex-col"
        panel-class="min-h-0 flex-1 overflow-y-auto pt-6">
        <!-- Overview: reuse + basics -->
        <template #tab-overview>
          <!-- Editable fields lead, the mapped EPD sits to their right as reference material at 2:1.
               items-start: grid items stretch by default, which grew the EPD card to the form's full height — it hugs its content now. -->
          <div class="grid grid-cols-1 items-start gap-6 md:grid-cols-[8fr_4fr]">
            <!-- Editable fields -->
            <div class="flex flex-col gap-4">
              <!-- Error state -->
              <div
                v-if="!cloneMaterial.newLifeTime && !isSaving && lifeTimeErrors.length > 0"
                class="bg-app-red-300/20 text-app-red-600 rounded-16 t-small flex flex-col gap-2 p-4">
                <span
                  v-for="error in lifeTimeErrors"
                  :key="error">
                  {{ error }}
                </span>
              </div>

              <!-- Material stage: life-cycle stage + reuse -->
              <MaterialStageSection
                :value="cloneMaterial.isRecycled"
                :stage="cloneMaterial.stage"
                :disabled="!canEdit"
                @update="(val: boolean) => updateIsRecycledValue(val)"
                @update-stage="updateStageValue" />

              <!-- Bim / Lifetime / Description -->
              <MaterialBasicsSection
                :material="cloneMaterial"
                :can-edit="canEdit"
                :read-only-tooltip="readOnlyTooltip"
                :selected-material-label="materialMappingIsType ? 'Material' : 'Type'"
                :life-time-errors="lifeTimeErrors"
                :life-time-overwrite="autoMappingLifeTime"
                :material-mapping-is-type="materialMappingIsType"
                :is-saving="isSaving"
                :life-time-updater-key="useMappingStore.getLifeTimeUpdater()"
                :speckle-classification-param="project?.speckleConfiguration?.parameters?.classificationCode"
                @toggle-lifetime-table="toggleLifetimeTableIsHidden"
                @set-bim-code="handleSetNewBimCode"
                @on-ccs-classification="onCCSClassification" />
            </div>

            <!-- Which EPD is used -->
            <EpdUsedBox
              :mapping="cloneMaterial.mapping"
              :is-inspecting="isInspecting"
              @inspect="handleMappingNameClicked" />
          </div>
        </template>

        <!-- Units -->
        <template #tab-units>
          <div class="flex flex-col gap-3">
            <!-- Right-aligned over the table's Original column: origin is a property of the
                 as-imported figures, so it reads as that side's caption, not a heading for the whole tab. -->
            <div
              v-if="cloneMaterial.source != null"
              class="flex items-center justify-end gap-2">
              <span class="t-caption text-type-muted">Imported from</span>
              <OriginTag :source="cloneMaterial.source" />
            </div>
            <UnitTable
              :clone-material="cloneMaterial"
              :total-declared-units="material.totalDeclaredUnits"
              :is-missing-unit="isMissingUnit"
              :user-can-edit="canEdit"
              :enable-unit-ids="enableUnitIds"
              @keyup-enter="submitSave" />
          </div>
        </template>

        <!-- Emissions -->
        <template #tab-emissions>
          <EmissionsPanel
            v-if="cloneMaterial.mapping"
            :material="cloneMaterial"
            :included-phases="project!.emissionCalculationAssumptions.emissionMetaData.includedPhases"
            :indicator-type="project!.calculationType?.thresholdIndicator.indicatorType"
            :consideration-period="project!.emissionCalculationAssumptions.calculationType.considerationPeriod"
            :unit="dashboard?.unit?.label ?? ''" />
        </template>
      </Tabs>

      <div class="mt-6 flex shrink-0 justify-between">
        <!-- aria-disabled, not the native attribute, so the tooltip still shows (see components.md);
             native `disabled` is kept only for the busy states, which have nothing to explain. -->
        <div
          v-if="cloneMaterial.source === 1 || cloneMaterial.source === 4 || cloneMaterial.mapping !== null"
          class="flex gap-3">
          <button
            v-if="cloneMaterial.source === 1 || cloneMaterial.source === 4"
            v-tooltip="readOnlyTooltip"
            type="button"
            :disabled="disableBtn"
            :aria-disabled="isReadOnly || undefined"
            :class="[btnBase, btnDestructive, { 'interactive-disabled': disableBtn || isReadOnly }]"
            @click="toggleDeleteModal">
            <Loader2
              v-if="isDelete"
              class="icon-sm animate-spin"
              :stroke-width="1.5" />
            Delete
          </button>
          <button
            v-if="cloneMaterial.mapping !== null && !autoMappingPreviewLoaded"
            v-tooltip="readOnlyTooltip"
            type="button"
            :disabled="disableBtn"
            :aria-disabled="isReadOnly || undefined"
            :class="[btnBase, btnNeutral, { 'interactive-disabled': disableBtn || isReadOnly }]"
            @click="removeMapping">
            <Loader2
              v-if="isBusy"
              class="icon-sm animate-spin"
              :stroke-width="1.5" />
            {{ isBusy ? "Removing Mapping..." : "Remove Mapping" }}
          </button>
        </div>

        <button
          v-tooltip="readOnlyTooltip"
          type="button"
          :disabled="disableBtn"
          :aria-disabled="isReadOnly || undefined"
          :class="[btnBase, btnPrimary, 'ml-auto', { 'interactive-disabled': disableBtn || isReadOnly }]"
          @click="submitSave">
          <Loader2
            v-if="isSaving"
            class="icon-sm animate-spin"
            :stroke-width="1.5" />
          {{ isSaving ? "Saving..." : "Save" }}
        </button>
      </div>
    </div>

    <!-- Inspected EPD as a view inside this modal; back pops one step. Scrolls on its own, as the
         card's overflow-hidden lets it shrink to this column and clip a construction's chart. -->
    <div
      v-if="inspectedEpd"
      class="scrollbar min-h-0 flex-1 overflow-y-auto">
      <LibraryDetailItem
        :material="inspectedEpd"
        :parent-name="cloneMaterial.identifier"
        :included-phases="project?.emissionCalculationAssumptions?.emissionMetaData?.includedPhases"
        @back="inspectStack.pop()"
        @on-click="(material: any) => inspectStack.push(material)" />
    </div>

    <LifetimeTable
      v-if="lifeTimeTableIsHidden"
      :title="cloneMaterial.identifier"
      :focus-construction-id="cloneMaterial.additionalParameters?.BIMTypeCode"
      :focus-construction-id-alternate="alternateBimTypeCode"
      @clear-select="lifeTimeTableIsHidden = false"
      @life-time-selected="
        (data: any) => {
          handleLifeTimeSelected(data.value);
        }
      " />
  </div>
</template>
